import { Info } from '@mui/icons-material';
import { Box } from '@mui/material';
import { SuppliersResponse } from 'lib/api/publicMarket/types';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import { Pagination } from 'lib/types/shared';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import GenerateLeadDialog from './Components/GenerateLeadDialog';

type Supplier = SuppliersResponse['data'][number];

interface Props {
  loading: boolean;
  data: Supplier[];
  setData: (data: SuppliersResponse['data']) => void;
  pagination: Pagination;
  setPagination: (pagination: Pagination) => void;
  fetchData: (params?: { page: number; pageSize: number }) => Promise<{
    data: SuppliersResponse['data'];
    pagination: Pagination;
  } | void>;
  openLeadDialog: boolean;
  setOpenLeadDialog: (open: boolean) => void;
  selectedSuppliers: Supplier[];
  setSelectedSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
}

const Suppliers: React.FC<Props> = ({
  loading,
  data,
  setData,
  pagination,
  setPagination,
  fetchData,
  openLeadDialog,
  setOpenLeadDialog,
  selectedSuppliers,
  setSelectedSuppliers,
}) => {
  const dispatch = useDispatch();
  const showSupplier = (id: string) => {
    const row = data.find((x: Supplier) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.identifier,
        name: row.name,
      }),
    );
  };

  const handleSelectSupplier = (ids: (string | number)[]) => {
    const idsAsString = ids.map((id) => String(id));

    setSelectedSuppliers((prev) => {
      const suppliersNotInCurrentPage = prev.filter(
        (supplier) => !data.some((d) => d.identifier === supplier.identifier),
      );
      const suppliersFromCurrentPage = data.filter((supplier) =>
        idsAsString.includes(String(supplier.identifier)),
      );
      return [...suppliersNotInCurrentPage, ...suppliersFromCurrentPage];
    });
  };

  const selectedIds = useMemo(
    () => selectedSuppliers.map((x: Supplier) => x.id),
    [selectedSuppliers],
  );

  const headers: Header[] = [
    { label: 'RUT', key: 'identifier' },
    {
      key: 'name',
      label: 'Emisor',
      handleAction: showSupplier,
      icon: <Info fontSize="small" />,
    },
    { label: 'Cant. Licit.', key: 'tenderCount' },
    {
      label: 'Monto Licit.',
      key: 'tenderAmount',
      type: 'money',
    },
    { label: 'Cant. Ordenes', key: 'purchaseOrderCount' },
    {
      label: 'Monto Ordenes',
      key: 'purchaseOrderAmount',
      type: 'money',
    },
    { label: 'F. Últ. Licit.', key: 'lastTenderDate', type: 'date' },
    { label: 'Cod. Últ. Licit.', key: 'lastTenderCode' },
    {
      label: 'F. Últ. Orden',
      key: 'lastPurchaseOrderDate',
      type: 'date',
    },
    { label: 'Cod. Últ. Orden', key: 'lastPurchaseOrderCode' },
    { label: 'Mis cuentas', key: 'isEnrolled', type: 'boolean-yes' },
  ];
  return (
    <>
      <Box sx={{ py: 2 }}>
        <TableWithPagination
          data={data}
          setData={setData}
          dataHeaders={headers}
          loading={loading}
          pagination={pagination}
          setPagination={setPagination}
          searchData={fetchData}
          exportToFile="xlsx"
          selected={selectedIds}
          setSelected={handleSelectSupplier}
          cantSelectRow={(row: Supplier) => row.isEnrolled}
        />
      </Box>
      <GenerateLeadDialog
        open={openLeadDialog}
        onClose={() => setOpenLeadDialog(false)}
        selectedSuppliers={selectedSuppliers}
        setSelectedSuppliers={setSelectedSuppliers}
      />
    </>
  );
};

export default Suppliers;
